import { CameraController } from './controls/CameraController';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Loader } from './utils/Loader';
import { GUI } from 'dat.gui';
import { Mouse } from './input/Mouse';
import { Keyboard } from './input/Keyboard';
import { CreatorAccessory, CreatorStep } from '@/types/creator';
import { EventEmitter } from './utils/EventEmitter';
import { SerializedRenderer, ThreeEvents } from './types';
import { Exporter, ExportType } from './utils/Exporter';
import { LightController } from './controls/LightsController';
import { RenderUtils } from './utils/RenderUtils';
import { HandleBuilder } from './builders/HandleBuilder';
import { ObjectsManager } from './managers/ObjectsManager';
import { HandleManager } from './managers/HandleManager';
import { Attachment, CreatorAction } from '@/types/types';
import { ActionHandler } from './utils/ActionHandler';
import {
	WebGLRenderer,
	Group,
	Scene,
	Color,
	SRGBColorSpace,
	ACESFilmicToneMapping,
	EquirectangularReflectionMapping,
} from 'three';
import { DoorManager } from './managers/DoorManager';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import envMapPath from '@/assets/hdri/studio.exr';
import APP_CONFIG from '@/configs/appConfig';
import Stats from 'three/examples/jsm/libs/stats.module.js';

const url = new URL(window.location.href);
const searchParams = new URLSearchParams(url.search);
const isDev = !!searchParams.get('DEV') || import.meta.env.DEV;

const stats = new Stats();
if (isDev) document.body.appendChild(stats.dom);

export class ProjectRenderer {
	private static instance: ProjectRenderer = new ProjectRenderer();
	private loader = Loader.getInstance();
	private exporter = new Exporter(this);
	private css2DRenderer!: CSS2DRenderer;
	isTouchDevice = false;
	isDev = isDev;
	actionHandler = new ActionHandler(this);
	handleManager!: HandleManager;
	objectsManager = new ObjectsManager(this);
	doorManager = new DoorManager(this);
	handleBuilder!: HandleBuilder;
	renderer!: WebGLRenderer;
	screenshotRenderer!: WebGLRenderer;
	lightsController!: LightController;
	cameraController!: CameraController;
	mouse = new Mouse(this);
	keyboard = new Keyboard(this);
	emitter = new EventEmitter<ThreeEvents>();
	loading = false;
	GUI!: GUI;
	project = new Group();
	scene!: Scene;
	canvas: HTMLCanvasElement | null = null;

	private constructor() {}

	static getInstance() {
		return ProjectRenderer.instance;
	}

	private initRenderers() {
		this.renderer = new WebGLRenderer({
			canvas: this.canvas!,
			antialias: true,
		});

		this.scene.background = new Color(RENDERER_CONFIG.COLORS.LIGHT_GRAY);

		const offscreenCanvas = new OffscreenCanvas(
			RENDERER_CONFIG.SCREENSHOT.WIDTH,
			RENDERER_CONFIG.SCREENSHOT.HEIGHT
		);

		this.screenshotRenderer = new WebGLRenderer({
			canvas: offscreenCanvas,
			antialias: true,
			preserveDrawingBuffer: true,
		});

		for (const renderer of [this.renderer, this.screenshotRenderer]) {
			renderer.outputColorSpace = SRGBColorSpace;
			renderer.toneMapping = ACESFilmicToneMapping;
			renderer.toneMappingExposure = 0.8;
		}

		this.css2DRenderer = new CSS2DRenderer();
		this.css2DRenderer.domElement.style.pointerEvents = 'none';
		this.canvas!.parentElement!.appendChild(this.css2DRenderer.domElement);
		this.css2DRenderer.domElement.style.zIndex =
			APP_CONFIG.Z_INDEX.CSS_2D_RENDERER.toString();
		this.css2DRenderer.domElement.style.position = 'absolute';
		this.css2DRenderer.domElement.style.top = '0px';

		this.renderer.setAnimationLoop(() => {
			if (import.meta.env.DEV) stats.begin();

			this.cameraController.controls.update();
			this.css2DRenderer.render(this.scene, this.cameraController.camera);
			this.renderer.render(this.scene, this.cameraController.camera);

			if (import.meta.env.DEV) stats.end();
		});
	}

	private loadEnvMap() {
		this.loader.eXRLoader.load(envMapPath, (texture) => {
			texture.mapping = EquirectangularReflectionMapping;

			this.scene.environment = texture;
			this.scene.environmentIntensity = 0.2;
		});
	}

	init(canvas: HTMLCanvasElement, isTouchDevice: boolean) {
		this.canvas = canvas;

		this.scene = new Scene();
		this.isTouchDevice = isTouchDevice;

		this.scene.add(this.project);

		this.cameraController = new CameraController(this);
		this.lightsController = new LightController(this);

		this.mouse.init();
		this.keyboard.init();

		this.initRenderers();
		this.loadEnvMap();

		this.handleManager = new HandleManager(this);
		this.handleBuilder = new HandleBuilder(
			this,
			this.handleManager,
			this.objectsManager
		);

		this.handleResize();
	}

	handleActions(creatorActions: CreatorAction[], cacheMaterialActions = true) {
		for (const action of creatorActions) {
			this.actionHandler.dispatchAction(action, cacheMaterialActions);
		}
	}

	handleCreatorStepChange(step: CreatorStep) {
		switch (step) {
			case CreatorStep.DIMENSIONS:
				break;
			case CreatorStep.ACCESSORIES:
				break;
			case CreatorStep.COLORS:
				break;
			case CreatorStep.SUMMARY:
				break;
		}
	}

	clear() {
		RenderUtils.clearGroup(this.project);

		this.objectsManager.clear();
		this.handleManager.clear();
		this.handleBuilder.measurementManager.clear();
		this.doorManager.selectedDoor = null;
	}

	async handleSelectVariation(model: Attachment) {
		this.clear();
		await this.handleManager.setModel(model.path);
	}

	async screenshot() {
		this.cameraController.prepareScreenshotCamera();
		this.screenshotRenderer.render(
			this.scene,
			this.cameraController.screenshotCamera
		);

		const canvas = this.screenshotRenderer
			.domElement as unknown as OffscreenCanvas;
		const blob = await canvas.convertToBlob();

		const file = new File([blob], `Podgląd konfiguracji.png`);

		return file;
	}

	handleCameraZoom(value: number) {
		this.cameraController.handleCameraZoom(value);
	}

	handleResize() {
		const { width, height } =
			this.canvas!.parentElement!.getBoundingClientRect();

		this.cameraController.handleResize(width / height);

		this.renderer.setSize(width, height);
		this.css2DRenderer.setSize(width, height);
	}

	async exportProjectModel(type: ExportType) {
		return await this.exporter.exportProject(type);
	}

	dispose() {
		this.renderer.dispose();
		this.cameraController.dispose();

		const ctx = this.canvas!.getContext('webgl2')!;
		ctx.clear(RENDERER_CONFIG.COLORS.LIGHT_CREAM);
	}

	export(): SerializedRenderer {
		return {
			handleManager: this.handleManager.export(),
			objectsManager: this.objectsManager.export(),
		};
	}

	async load(data: SerializedRenderer, creatorAccessories: CreatorAccessory[]) {
		this.clear();
		await this.handleManager.load(data.handleManager);
		this.objectsManager.load(data.objectsManager, creatorAccessories);
	}
}
